import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";

import FormatCrypto from "@hl/base-components/lib/FormatCrypto";
import {
  SwitchVertical01,
  SwitchVertical02,
} from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Arrows";
import {
  Cryptocurrency02,
  Diamond01,
} from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Finance & eCommerce";
import { Hourglass01 } from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Time";
import Stars from "@hl/base-components/lib/assets/icons.generated/Stars";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import {
  TEXT_COLOR,
  UTILITY_COLOR,
} from "@hl/base-components/lib/theme/colors";
import {
  WEIGHT_BOLD,
  WEIGHT_NORMAL,
} from "@hl/base-components/lib/theme/typography";
import { OfferCriteriaKind } from "@hl/shared-features/lib/apollo/graphql.generated";
import { User } from "@hl/shared-features/lib/features/auth/User";
import {
  QuicksellBid,
  useQuicksellCartContext,
} from "@hl/shared-features/lib/features/cart/QuickSellProvider";
import { NoResults } from "@hl/shared-features/lib/features/collections/NoResults";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import CryptoCurrencyInput from "@hl/shared-features/lib/features/input/CryptoCurrencyInput";
import { adjustIconSize } from "@hl/shared-features/lib/features/marketplace/components/MarketplaceFilterSortBarApollo";
import MarketplacePrice from "@hl/shared-features/lib/features/marketplace/components/MarketplacePrice";
import {
  MarketplaceStickyHeader,
  SearchInput,
} from "@hl/shared-features/lib/features/marketplace/components/MarketplaceStickyHeader";
import { QuickSellDrawer } from "@hl/shared-features/lib/features/marketplace/drawers/QuickSellDrawer";
import {
  useGetUserTotalOffersQuery,
  useOwnerOffersQuery,
} from "@hl/shared-features/lib/features/marketplace/queries/marketplace.graphql.generated";
import { OwnerOffer } from "@hl/shared-features/lib/features/marketplace/utils";
import usePercentDifference from "@hl/shared-features/lib/hooks/usePercentDifference";
import { SortingOption } from "@hl/shared-features/lib/utils/sortingOptions";
import {
  Anchor,
  Box,
  Button,
  Checkbox,
  createStyles,
  Group,
  GroupProps,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import dayjs, { extend } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import InfiniteScroll from "react-infinite-scroll-component";

import { getDetailPageUrl, getMintPageUrl } from "~config";

import {
  OrderSortOptions,
  SortDirection,
} from "../../apollo/graphql.generated";
import { OnLoadProps } from "../profile/UserTabs";

import { Loading, shimmerStyles } from "./Collected";

extend(relativeTime);
extend(advancedFormat);
extend(utc);
extend(timezone);

const useStyles = createStyles({
  link: {
    textDecoration: "none",
  },
  input: {
    height: "40px",
    marginTop: 0,
    fontWeight: WEIGHT_BOLD,
    minWidth: 150,
  },
  root: { flexGrow: 1, width: "100%" },
});

const sortingOptions: SortingOption<OrderSortOptions>[] = [
  {
    value: "price_desc",
    label: "Highest price",
    sortBy: OrderSortOptions.PRICE,
    sortDirection: SortDirection.DESC,
    icon: SwitchVertical01,
  },
  {
    value: "price_asc",
    label: "Lowest price",
    sortBy: OrderSortOptions.PRICE,
    sortDirection: SortDirection.ASC,
    icon: SwitchVertical02,
  },
  {
    value: "exp_asc",
    label: "Expiring soonest",
    sortBy: OrderSortOptions.EXPIRING,
    sortDirection: SortDirection.ASC,
    icon: Cryptocurrency02,
  },
  {
    value: "date_desc",
    label: "Newest first",
    sortBy: OrderSortOptions.EXPIRING,
    sortDirection: SortDirection.DESC,
    icon: Hourglass01,
  },
  {
    value: "highest_above_floor",
    label: "Highest above floor",
    sortBy: OrderSortOptions.HIGHEST_ABOVE_FLOOR,
    sortDirection: SortDirection.DESC,
    icon: Diamond01,
  },
];

type TemporaryTypes = "best-offers" | "all-offers" | OfferCriteriaKind;

export type ReceivedOffersFilter<T = TemporaryTypes> = {
  value: T;
  label: string;
  icon?: React.FC;
  clientOnly?: boolean;
};

const receivedOffersFilterOptions: ReceivedOffersFilter[] = [
  {
    value: "best-offers",
    label: "Best offers",
    icon: Stars,
    clientOnly: true,
  },
  {
    value: "all-offers",
    label: "All offers",
    clientOnly: true,
  },
  {
    value: OfferCriteriaKind.COLLECTION,
    label: "Collection offers",
  },
  {
    value: OfferCriteriaKind.TRAIT,
    label: "Trait offers",
  },
  {
    value: OfferCriteriaKind.TOKEN,
    label: "Item offers",
  },
];

export const ReceivedOffers = ({ onLoad }: OnLoadProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [searchString, setSearchString] = useState<string>("");
  const [minPrice, setMinPrice] = useState<number>();
  const [percentDifference, setPercentDifference] = useState<number>();
  const [debouncedMinPrice] = useDebouncedValue<number | undefined>(
    minPrice,
    250
  );
  const [debouncedPercentDifference] = useDebouncedValue<number | undefined>(
    percentDifference,
    250
  );
  const [debouncedSearchString] = useDebouncedValue(searchString, 250);
  const [sorting, setSorting] = useState<string | null>(
    sortingOptions[0].value
  );
  const { state, dispatch } = useQuicksellCartContext();

  const [receivedOffersFilterKey, setReceivedOffersFilterKey] = useState<
    string | null
  >(receivedOffersFilterOptions[0].value);
  const { classes, theme } = useStyles();
  const { isSmall, isMedium } = useMantineMediaQueries({
    withInitialValues: true,
  });
  const [aboveFloor, setAboveFloor] = useState<boolean | null>(null);
  const selectedSort = useMemo(
    () =>
      sortingOptions.find((sortingOption) => sortingOption.value === sorting),
    [sorting]
  );
  const selectedReceivedOffersFilter = useMemo(
    () =>
      receivedOffersFilterOptions.find(
        (filter) => filter.value === receivedOffersFilterKey
      ),
    [receivedOffersFilterKey]
  );
  const isBestOffers = selectedReceivedOffersFilter?.value === "best-offers";

  const criteriaKindV2 = selectedReceivedOffersFilter?.clientOnly
    ? null
    : (selectedReceivedOffersFilter?.value as OfferCriteriaKind) || null;
  const criteriaKind = criteriaKindV2;

  useEffect(() => {
    setMinPrice(undefined);
    setSorting(sortingOptions[0].value);
    setAboveFloor(null);
    setPercentDifference(undefined);
  }, [isBestOffers]);

  const { data, fetchMore, loading, error, refetch } = useOwnerOffersQuery({
    variables: {
      chainId: null,
      createdAfter: null,
      tokenId: null,
      tokenAddress: null,
      aboveFloor,
      tokenOrCollectionName: debouncedSearchString,
      sortDirection: selectedSort!.sortDirection,
      sortBy: selectedSort!.sortBy,
      after: null,
      first: 10,
      minPrice: debouncedMinPrice ? String(debouncedMinPrice) : null,
      floorPriceDifference: isBestOffers
        ? "15"
        : debouncedPercentDifference
        ? String(debouncedPercentDifference)
        : null,
      criteriaKind,
    },
  });

  const { data: totalSellResponse, loading: loadingTotalSellValue } =
    useGetUserTotalOffersQuery();
  const totalSell = totalSellResponse?.getUserTotalOffers[0];
  const totalSellValue = totalSell?.native;
  const totalSellSymbol = totalSell?.symbol;

  useEffect(() => {
    const totalCount = data?.getOwnerOffers.pageInfo.totalCount;
    if (totalCount != null) {
      onLoad?.({ totalCount });
    }
  }, [data?.getOwnerOffers.pageInfo.totalCount]);

  const bids = useMemo(
    () => data?.getOwnerOffers?.edges || [],
    [data?.getOwnerOffers?.edges]
  );

  const nextCursor = data?.getOwnerOffers?.pageInfo.endCursor;
  const hasNextPage = data?.getOwnerOffers?.pageInfo?.hasNextPage;
  const emptyCart = () => dispatch({ type: "REMOVE_ALL_BIDS" });
  const openCart = () => {
    dispatch({ type: "OPEN_DRAWER" });
  };

  const tableHeaders = (
    <tr>
      <th>Offer Price</th>
      <th>Item</th>
      <th>Expires In</th>
      <th>From</th>
      <th style={{ textAlign: "right" }}>
        {state.opened && (
          <UnstyledButton onClick={emptyCart}>
            <Text size="sm">Deselect all</Text>
          </UnstyledButton>
        )}
      </th>
    </tr>
  );

  const tableRows = bids.map((bid) => (
    <ReceivedOfferRow bid={bid} key={bid.reservoirId} />
  ));

  const sellValueButton = !loadingTotalSellValue && (
    <Button
      onClick={() => {
        setTimeout(
          () =>
            ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
          200
        );
        setReceivedOffersFilterKey("best-offers");
        openCart();
      }}
      disabled={state.opened}
      styles={{
        // @ts-ignore
        root: {
          ...(!state.opened ? shimmerStyles() : {}),
          backgroundColor: theme.colors.primaryButton[0],
          transition: "background-color 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: theme.colors.primaryButton[1],
          },
        },
        label: {
          position: "relative",
          zIndex: 1,
        },
      }}
    >
      Quick sell{" "}
      {totalSellValue ? `(Offers > ${totalSellValue} ${totalSellSymbol})` : ""}
    </Button>
  );

  const isSearching =
    !!debouncedSearchString ||
    !!debouncedMinPrice ||
    !!debouncedPercentDifference;

  return (
    <Stack ref={ref}>
      <QuickSellDrawer
        onCompleted={() =>
          setTimeout(() => {
            refetch();
          }, 3000)
        }
      />

      <MarketplaceStickyHeader py={16}>
        <Group noWrap={isMedium} spacing={8}>
          <SearchInput
            w={{ base: "100%", sm: 200, md: 250 }}
            sx={{ flexGrow: 1 }}
            placeholder="Filter offers by collection name"
            value={searchString}
            onChange={setSearchString}
          />
          <Select
            icon={selectedSort ? adjustIconSize(selectedSort.icon) : null}
            value={sorting}
            onChange={setSorting}
            data={sortingOptions}
            sx={{ flexGrow: 1 }}
            itemComponent={(props) => (
              <SelectItem {...props} selected={sorting} />
            )}
            classNames={{ input: classes.input }}
          />
          <Select
            icon={
              selectedReceivedOffersFilter?.icon
                ? adjustIconSize(selectedReceivedOffersFilter.icon)
                : null
            }
            iconWidth={50}
            value={receivedOffersFilterKey}
            onChange={setReceivedOffersFilterKey}
            data={receivedOffersFilterOptions}
            sx={{ flexGrow: 1 }}
            itemComponent={(props) => (
              <SelectItem {...props} selected={receivedOffersFilterKey} />
            )}
            classNames={{ input: classes.input }}
          />
          {isSmall && (
            <>
              <NumberInput
                placeholder="Floor price difference"
                size="sm"
                sx={{ flexGrow: 1, minWidth: 200 }}
                min={0}
                max={100}
                onChange={(val) => {
                  if (!val || (val >= 0 && val <= 100)) {
                    setPercentDifference(val);
                  }
                  if (isBestOffers) {
                    setReceivedOffersFilterKey("all-offers");
                  }
                }}
                value={percentDifference}
                step={1}
                hideControls
                rightSection={
                  <Text size="sm" color={TEXT_COLOR.SECONDARY}>
                    %
                  </Text>
                }
                classNames={{ input: classes.input }}
              />
              <CryptoCurrencyInput
                chainId={1}
                missingChainIdPlaceholder=""
                placeholder="Min price"
                size="sm"
                sx={{ flexGrow: 1 }}
                onChange={setMinPrice}
                value={minPrice}
                returnEmpty
                classNames={{ input: classes.input }}
              />
            </>
          )}
          {!isMedium && sellValueButton}
        </Group>
        {isMedium && sellValueButton}
      </MarketplaceStickyHeader>
      {loading ? (
        <Loading />
      ) : bids.length ? (
        <InfiniteScroll
          dataLength={bids.length}
          next={() =>
            hasNextPage &&
            fetchMore({
              variables: { after: nextCursor },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                fetchMoreResult!.getOwnerOffers.edges = [
                  ...(previousResult?.getOwnerOffers?.edges ?? []),
                  ...(fetchMoreResult?.getOwnerOffers?.edges ?? []),
                ];
                return fetchMoreResult;
              },
            })
          }
          hasMore={!!hasNextPage}
          loader={<Loading />}
        >
          <Table
            mb={state.drawerSize}
            verticalSpacing="md"
            miw={1200}
            sx={{
              "thead tr th": {
                color: theme.colors.secondaryText[0],
                paddingLeft: 0,
                fontSize: 14,
                fontWeight: WEIGHT_NORMAL,
                paddingTop: 8,
              },
              "tbody tr td": {
                paddingLeft: 0,
              },
              "tbody tr td:last-child": {
                paddingRight: 0,
              },
            }}
          >
            <thead>{tableHeaders}</thead>
            <tbody>{tableRows}</tbody>
          </Table>
        </InfiniteScroll>
      ) : !!error ? (
        <NoResults
          title="Something went wrong"
          description="Please try again later"
        />
      ) : (
        <NoResults
          description={
            isBestOffers
              ? "Click below to see all offers"
              : isSearching
              ? "Try adjusting your filter settings"
              : "Once you do, they'll appear here."
          }
          title={
            isBestOffers
              ? "No “Best offers” (within 15% of floor prices)"
              : isSearching
              ? "No offers found"
              : "No offers received yet"
          }
          label={isBestOffers ? "See all offers" : ""}
          onClickLabel={
            isBestOffers
              ? () => setReceivedOffersFilterKey("all-offers")
              : undefined
          }
        />
      )}
    </Stack>
  );
};

function ReceivedOfferRow({ bid }: { bid: OwnerOffer }) {
  const { dispatch, state } = useQuicksellCartContext();
  const theme = useMantineTheme();
  const {
    price,
    floorPrice,
    tokens,
    collection,
    quantityRemaining,
    createdAt,
    criteriaKind,
    expiredAt,
    user,
    makerAddress,
    attributeValue,
    attributeKey,
  } = bid;
  const isBeingSold = !!state.bids.find(
    (el) => el.reservoirId === bid.reservoirId
  );
  const removeBid = () => {
    dispatch({
      type: "REMOVE_BID",
      payload: { reservoirId: bid.reservoirId },
    });
  };

  const { diffWording } = usePercentDifference(
    price?.amount?.native,
    floorPrice,
    true
  );
  const { classes } = useStyles();
  const token = tokens?.[0];
  const tokenId = token?.id ?? "";
  const collectionUrl = collection ? getMintPageUrl(collection) : "";

  const tokenUrl =
    collection && tokenId
      ? getDetailPageUrl(collection, tokenId)
      : collectionUrl;

  const acceptBid = () => {
    const tokenId =
      bid.tokens?.[0]?.tokenRevealId ?? bid.tokens?.[0]?.id ?? undefined;

    const newBid: QuicksellBid = {
      id: bid.id,
      chainId: bid.chainId,
      address: bid.collection?.address ?? "",
      quantity: 1,
      royaltyBps: 0,
      editionId: collection?.editionId ?? undefined,
      reservoirId: bid.reservoirId,
      tokenId: bid.criteriaKind === "token" ? tokenId : undefined,
      bid,
      token: { ...token, tokenId: token?.tokenRevealId ?? "" },
    };
    dispatch({ type: "ADD_BIDS", payload: [newBid] });
  };

  const src = token?.imageUrl ?? collection?.imageUrl ?? "";
  const fp = floorPrice && floorPrice != "0" ? parseFloat(floorPrice) : null;
  const toggleBid = () => {
    if (isBeingSold) {
      removeBid();
    } else {
      acceptBid();
    }
  };

  return (
    <tr
      key={createdAt}
      style={{
        transition: "background 0.1s",
        background: isBeingSold
          ? theme.colors.secondaryBackground[0]
          : undefined,
      }}
    >
      <td style={{ paddingLeft: 16 }}>
        <Stack spacing={4}>
          <MarketplacePrice price={price} />
          {fp && (
            <Text size="sm" color={TEXT_COLOR.SECONDARY}>
              <FormatCrypto
                amount={fp}
                onlyText
                symbol={bid.price.currency.symbol}
              />{" "}
              floor ({diffWording})
            </Text>
          )}
        </Stack>
      </td>
      <td>
        <Group noWrap>
          <Box p={8} bg={UTILITY_COLOR.TABLE_ROW_BACKGROUND}>
            <ResizedImage src={src} width={64} height={64} fit="contain" />
          </Box>
          <Stack spacing={4}>
            <Anchor href={tokenUrl} className={classes.link}>
              <Group spacing="xs">
                <Text fw={WEIGHT_BOLD}>
                  {criteriaKind === "token" && token?.name}
                  {criteriaKind === "attribute" &&
                    `Trait: ${attributeKey} ${attributeValue}`}
                  {criteriaKind === "collection" && "Collection offer"}
                </Text>
                {quantityRemaining > 1 && <Text>x{quantityRemaining}</Text>}
              </Group>
            </Anchor>
            <Anchor href={collectionUrl} className={classes.link}>
              <Text size="sm">{collection?.name}</Text>
            </Anchor>
          </Stack>
        </Group>
      </td>
      <td>
        {expiredAt && (
          <Stack spacing={4}>
            <Text>
              {dayjs().isAfter(expiredAt) ? "Expired" : dayjs().to(expiredAt)}
            </Text>
            <Text size="sm" color={TEXT_COLOR.SECONDARY}>
              {dayjs(expiredAt).format("MMM. DD @ h:mma z")}
            </Text>
          </Stack>
        )}
      </td>
      <td>
        <User
          enableLink={!!user?.id}
          textSize="md"
          walletAddresses={[makerAddress]}
        />
      </td>
      <td align="right" style={{ paddingRight: 16 }}>
        {!dayjs().isAfter(expiredAt) && !state.opened ? (
          <Button
            size="lg"
            px={12}
            color={SECONDARY_COLOR}
            disabled={dayjs().isAfter(expiredAt)}
            onClick={acceptBid}
          >
            Accept offer
          </Button>
        ) : (
          !dayjs().isAfter(expiredAt) && (
            <Checkbox
              checked={
                !!state.bids.find((b) => b.reservoirId === bid.reservoirId)
              }
              onChange={() => toggleBid()}
            />
          )
        )}
      </td>
    </tr>
  );
}

type ItemProps = SortingOption & GroupProps & { selected: string };

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ icon, label, ...others }: ItemProps, ref) => (
    <Group
      noWrap
      ref={ref}
      {...others}
      spacing={8}
      sx={{ justifyContent: "center" }}
    >
      {icon ? adjustIconSize(icon) : null}
      <Text size="sm">{label}</Text>
    </Group>
  )
);
